import type { ClipboardEvent } from "react";
import { Clipboard } from "lucide-react";
import { MAX_TEXT_CHARS } from "../shared";
import { Button } from "./ui/Button";

type TextStats = {
  characters: number;
  words: number;
  lines: number;
};

export function EditorPanel({
  text,
  stats,
  onTextChange,
  onPasteFiles,
  onCopyText,
}: {
  text: string;
  stats: TextStats;
  onTextChange: (value: string) => void;
  onPasteFiles: (files: File[]) => void;
  onCopyText: () => Promise<void>;
}) {
  const handlePaste = (event: ClipboardEvent<HTMLTextAreaElement>) => {
    const files = Array.from(event.clipboardData.files);
    if (files.length) {
      event.preventDefault();
      onPasteFiles(files);
    }
  };

  return (
    <section className="grid min-h-[520px] min-w-0 grid-rows-[auto_minmax(0,1fr)_auto] overflow-hidden rounded-lg border border-neutral-200 bg-white shadow-[0_1px_2px_rgba(0,0,0,0.04),0_8px_24px_rgba(0,0,0,0.03)] lg:min-h-0">
      <header className="flex min-h-14 items-center justify-between border-b border-neutral-200 px-4">
        <h1 className="text-sm font-semibold text-neutral-950">Shared Text</h1>
        <span className="text-xs text-neutral-500">Plain Text</span>
      </header>
      <textarea
        value={text}
        maxLength={MAX_TEXT_CHARS}
        onChange={(event) => onTextChange(event.target.value)}
        onPaste={handlePaste}
        spellCheck={false}
        placeholder="Start typing…"
        aria-label="Shared text"
        className="min-h-[420px] w-full resize-none border-0 bg-transparent p-5 font-mono text-[15px] leading-7 text-neutral-950 outline-none placeholder:text-neutral-400 focus-visible:ring-0 sm:p-7 sm:text-base lg:min-h-0"
      />
      <footer className="flex min-h-14 flex-wrap items-center gap-3 border-t border-neutral-200 px-4 py-3 text-sm text-neutral-500">
        <span>{stats.characters} characters</span>
        <span>{stats.words} words</span>
        <span>{stats.lines} lines</span>
        <span className="flex-1" />
        <Button onClick={() => void onCopyText()} size="sm" variant="secondary">
          <Clipboard size={16} />
          Copy text
        </Button>
      </footer>
    </section>
  );
}
